import React from 'react';
import html2canvas from 'html2canvas';
import { Download, Loader2, X } from 'lucide-react';
import { Button } from '@daater/ui';
import type {
  AnalyticsDimensionResult,
  ColumnMapping,
  RecordsFilter,
  TableField,
  TableFilter,
} from '../api/types';
import { useTableAnalyticsQuery } from '../api/queries';
import type { AnalyticsRequest } from '../api/types';
import {
  AdvancedDynamicRow,
  DynamicRowSelector,
  TrendRow,
  applyDrillPath,
  aggregateByDimension,
  aggregateTrend,
  buildDimensionOptions,
  buildMetricOptions,
  buildMetricOptionsFromFields,
  buildRowId,
  detectDateAccessor,
  detectDateAccessorFromFields,
  detectExchangeRateAccessor,
  downloadAdvancedAnalysisExcel,
  getCurrencyUnitOptions,
  getTrendUnitCategory,
  getUnitOptionsByCategory,
  isNumericFieldAccessor,
  toggleOrSetDrillStep,
  type AdvancedRowDefinition,
  type ChartImage,
  type DrillStep,
  type ExcelSection,
  type ForecastConfig,
  type TrendDisplayUnit,
  type TrendGrain,
} from './advanced-charts';

type AnalysisSectionProps = {
  records: Array<Record<string, unknown>>;
  mapping?: ColumnMapping[];
  fields?: TableField[];
  filters?: TableFilter[];
  loading?: boolean;
  storageKey?: string;
  tableId?: string;
  appliedFilters?: RecordsFilter[];
};

type PersistedAnalysisState = {
  trendMetricKey: string;
  trendUnit: TrendDisplayUnit;
  trendGrain: TrendGrain;
  forecastConfig: ForecastConfig;
  dynamicRows: AdvancedRowDefinition[];
  drillPath: DrillStep[];
};

const STORAGE_PREFIX = 'daater:advanced-analysis:v2';
const DEFAULT_CHART_TYPE = 'bar-horizontal' as const;
const DEFAULT_FORECAST_CONFIG: ForecastConfig = {
  method: 'linear',
  horizon: 6,
  confidenceZ: 1.645,
  clampLowerToZero: true,
};

const normalizeChartType = (value: unknown) => {
  if (
    value === 'bar-horizontal' ||
    value === 'bar-vertical' ||
    value === 'pie' ||
    value === 'treemap'
  ) {
    return value;
  }
  return DEFAULT_CHART_TYPE;
};

type DynamicRowWithRefProps = {
  row: AdvancedRowDefinition;
  filteredRecords: Array<Record<string, unknown>>;
  dimensionOptions: { key: string; label: string }[];
  metricOptions: { key: string; label: string; unitDefault: string }[];
  exchangeRateAccessor?: string;
  precomputedBackendDimension?: AnalyticsDimensionResult;
  onRemove: (id: string) => void;
  onChange: (id: string, patch: Partial<AdvancedRowDefinition>) => void;
  drillPath: DrillStep[];
  activePanelDimension?: string;
  onSelectRow: (payload: {
    dimensionAccessor: string;
    rowKey: string;
    label: string;
    secondaryLabel?: string;
  }) => void;
  onClearDimensionSelection: (dimensionAccessor: string) => void;
  chartRefs: React.RefObject<Map<string, HTMLDivElement | null>>;
};

function DynamicRowWithRef({
  row,
  filteredRecords,
  dimensionOptions,
  metricOptions,
  exchangeRateAccessor,
  precomputedBackendDimension,
  onRemove,
  onChange,
  drillPath,
  activePanelDimension,
  onSelectRow,
  onClearDimensionSelection,
  chartRefs,
}: DynamicRowWithRefProps) {
  const chartRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const refs = chartRefs.current;
    refs.set(row.id, chartRef.current);
    return () => {
      refs.delete(row.id);
    };
  }, [row.id, chartRefs]);

  return (
    <AdvancedDynamicRow
      definition={row}
      records={filteredRecords}
      dimensions={dimensionOptions}
      metricOptions={metricOptions}
      exchangeRateAccessor={exchangeRateAccessor}
      precomputedBackendDimension={precomputedBackendDimension}
      onRemove={onRemove}
      onChange={onChange}
      drillPath={drillPath}
      isPanelActive={activePanelDimension === row.dimensionAccessor}
      onSelectRow={onSelectRow}
      onClearDimensionSelection={onClearDimensionSelection}
      chartRef={chartRef}
    />
  );
}

export default function AnalysisSection({
  records,
  mapping,
  fields,
  filters,
  loading,
  storageKey,
  tableId,
  appliedFilters,
}: AnalysisSectionProps) {
  const dimensionOptions = React.useMemo(
    () => buildDimensionOptions(mapping, records, fields, filters),
    [mapping, records, fields, filters]
  );
  const metricOptionsFromRecords = React.useMemo(
    () => buildMetricOptions(mapping, records),
    [mapping, records]
  );
  // El tipo del campo manda sobre la heurística de valores: una columna de texto cuyos valores
  // parecen números rompía el gráfico en el backend (`function sum(text) does not exist`).
  const metricOptions = React.useMemo(() => {
    if (fields?.length) {
      const fromFields = buildMetricOptionsFromFields(fields);
      if (fromFields.length > 0) return fromFields;
      return [];
    }
    return metricOptionsFromRecords;
  }, [metricOptionsFromRecords, fields]);
  const dateAccessorFromFields = React.useMemo(
    () => (fields?.length ? detectDateAccessorFromFields(fields) : ''),
    [fields]
  );
  const dateAccessorFromRecords = React.useMemo(
    () => detectDateAccessor(mapping, records),
    [mapping, records]
  );
  const dateAccessor = dateAccessorFromFields || dateAccessorFromRecords;
  const exchangeRateAccessor = React.useMemo(() => {
    const detected = detectExchangeRateAccessor(mapping, records);
    return isNumericFieldAccessor(fields, detected) ? detected : '';
  }, [mapping, records, fields]);

  const defaultMetric = metricOptions[0];
  const [trendMetricKey, setTrendMetricKey] = React.useState('');
  const [trendUnit, setTrendUnit] = React.useState<TrendDisplayUnit>('USD');
  const [trendGrain, setTrendGrain] = React.useState<TrendGrain>('month');
  const [forecastConfig, setForecastConfig] =
    React.useState<ForecastConfig>(DEFAULT_FORECAST_CONFIG);
  const [dynamicRows, setDynamicRows] = React.useState<AdvancedRowDefinition[]>([]);
  const [drillPath, setDrillPath] = React.useState<DrillStep[]>([]);
  const [hasHydratedStorage, setHasHydratedStorage] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const trendChartRef = React.useRef<HTMLDivElement>(null);
  const dynamicChartRefs = React.useRef<Map<string, HTMLDivElement | null>>(new Map());

  const persistedStorageKey = React.useMemo(() => {
    if (!storageKey) return '';
    return `${STORAGE_PREFIX}:${storageKey}`;
  }, [storageKey]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !persistedStorageKey) {
      setHasHydratedStorage(true);
      return;
    }

    try {
      const raw = window.localStorage.getItem(persistedStorageKey);
      if (!raw) {
        setHasHydratedStorage(true);
        return;
      }

      const parsed = JSON.parse(raw) as PersistedAnalysisState;
      if (parsed.trendMetricKey) setTrendMetricKey(parsed.trendMetricKey);
      if (parsed.trendUnit) setTrendUnit(parsed.trendUnit);
      if (parsed.trendGrain) setTrendGrain(parsed.trendGrain);
      if (parsed.forecastConfig) {
        setForecastConfig(prev => ({
          ...prev,
          ...parsed.forecastConfig,
          ...DEFAULT_FORECAST_CONFIG,
        }));
      }
      if (Array.isArray(parsed.dynamicRows)) {
        setDynamicRows(
          parsed.dynamicRows.slice(0, 12).map(row => ({
            ...row,
            chartType: normalizeChartType((row as { chartType?: unknown }).chartType),
          }))
        );
      }
      if (Array.isArray(parsed.drillPath)) {
        setDrillPath(parsed.drillPath.slice(0, 6));
      }
    } catch {
      // If state cannot be parsed we fall back to defaults.
    } finally {
      setHasHydratedStorage(true);
    }
  }, [persistedStorageKey]);

  React.useEffect(() => {
    if (!defaultMetric) return;
    setTrendMetricKey(prev => prev || defaultMetric.key);
  }, [defaultMetric]);

  React.useEffect(() => {
    if (!metricOptions.length) return;
    const validMetricKeys = new Set(metricOptions.map(metric => metric.key));
    const fallbackMetric = metricOptions[0];

    setTrendMetricKey(prev => (validMetricKeys.has(prev) ? prev : fallbackMetric.key));
    setDynamicRows(prev =>
      prev.map(row => {
        if (validMetricKeys.has(row.metricKey)) return row;
        return {
          ...row,
          metricKey: fallbackMetric.key,
          unit: fallbackMetric.unitDefault,
        };
      })
    );
  }, [metricOptions]);

  const selectedTrendMetric = React.useMemo(
    () => metricOptions.find(metric => metric.key === trendMetricKey),
    [metricOptions, trendMetricKey]
  );

  const trendUnitOptions = React.useMemo(() => {
    const category = getTrendUnitCategory(selectedTrendMetric?.label ?? trendMetricKey);
    if (category === 'currency') {
      return getCurrencyUnitOptions(Boolean(exchangeRateAccessor));
    }
    return getUnitOptionsByCategory(category);
  }, [selectedTrendMetric?.label, trendMetricKey, exchangeRateAccessor]);

  React.useEffect(() => {
    if (!trendUnitOptions.length) return;
    setTrendUnit(prev => (trendUnitOptions.includes(prev) ? prev : trendUnitOptions[0]));
  }, [trendUnitOptions]);

  React.useEffect(() => {
    if (!dimensionOptions.length) return;
    const validDimensions = new Set(dimensionOptions.map(option => option.key));
    setDynamicRows(prev =>
      prev.filter(row => validDimensions.has(row.dimensionAccessor)).slice(0, 12)
    );
    setDrillPath(prev =>
      prev.filter(step => validDimensions.has(step.dimensionAccessor)).slice(0, 6)
    );
  }, [dimensionOptions]);

  const filteredRecords = React.useMemo(
    () => applyDrillPath(records, drillPath),
    [records, drillPath]
  );

  const analyticsBody = React.useMemo((): AnalyticsRequest | null => {
    if (!tableId || !appliedFilters?.length) return null;
    const hasTrend = Boolean(dateAccessor && trendMetricKey);
    const hasDimensions = dynamicRows.length > 0;
    if (!hasTrend && !hasDimensions) return null;

    const drillFilters = drillPath.map(step => ({
      column: step.dimensionAccessor,
      value: step.valueKey,
    }));

    return {
      filters: appliedFilters,
      drillFilters: drillFilters.length > 0 ? drillFilters : undefined,
      trend: hasTrend
        ? {
            dateColumn: dateAccessor,
            metricColumn: trendMetricKey,
            grain: trendGrain,
            ...(trendUnit === 'COP' && exchangeRateAccessor
              ? { exchangeRateColumn: exchangeRateAccessor }
              : {}),
          }
        : undefined,
      dimensions:
        dynamicRows.length > 0
          ? dynamicRows.map(row => ({
              id: row.id,
              dimensionColumn: row.dimensionAccessor,
              metricColumn: row.metricKey,
              topN: row.topN,
            }))
          : undefined,
    };
  }, [
    tableId,
    appliedFilters,
    dateAccessor,
    trendMetricKey,
    trendGrain,
    trendUnit,
    exchangeRateAccessor,
    dynamicRows,
    drillPath,
  ]);

  const { data: analyticsData } = useTableAnalyticsQuery(tableId, analyticsBody);

  const selectedDimensionKeys = React.useMemo(
    () => dynamicRows.map(row => row.dimensionAccessor),
    [dynamicRows]
  );

  const addDynamicRow = React.useCallback(
    (dimensionKey: string) => {
      const dimension = dimensionOptions.find(option => option.key === dimensionKey);
      const metric =
        metricOptions.find(option => option.key === trendMetricKey) ?? metricOptions[0];
      if (!dimension || !metric) return;

      setDynamicRows(prev => {
        if (prev.some(item => item.dimensionAccessor === dimension.key)) return prev;
        if (prev.length >= 12) return prev;
        return [
          ...prev,
          {
            id: buildRowId(),
            dimensionFieldId: dimension.key,
            dimensionAccessor: dimension.key,
            metricKey: trendMetricKey || metric.key,
            unit: trendUnit,
            topN: 10,
            chartType: DEFAULT_CHART_TYPE,
            sortBy: 'metric-desc',
          },
        ];
      });
    },
    [dimensionOptions, metricOptions, trendMetricKey, trendUnit]
  );

  const removeDynamicRow = React.useCallback((id: string) => {
    setDynamicRows(prev => prev.filter(row => row.id !== id));
  }, []);

  const removeDynamicRowByDimension = React.useCallback((dimensionKey: string) => {
    setDynamicRows(prev => prev.filter(row => row.dimensionAccessor !== dimensionKey));
  }, []);

  const patchDynamicRow = React.useCallback(
    (id: string, patch: Partial<AdvancedRowDefinition>) => {
      setDynamicRows(prev =>
        prev.map(row =>
          row.id === id
            ? {
                ...row,
                ...patch,
                metricKey: trendMetricKey || row.metricKey,
                unit: trendUnit,
              }
            : row
        )
      );
    },
    [trendMetricKey, trendUnit]
  );

  React.useEffect(() => {
    if (!trendMetricKey) return;
    setDynamicRows(prev =>
      prev.map(row =>
        row.metricKey === trendMetricKey && row.unit === trendUnit
          ? row
          : {
              ...row,
              metricKey: trendMetricKey,
              unit: trendUnit,
            }
      )
    );
  }, [trendMetricKey, trendUnit]);

  const captureChartImage = React.useCallback(
    async (element: HTMLElement | null, title: string): Promise<ChartImage | null> => {
      if (!element) return null;
      try {
        const canvas = await html2canvas(element, {
          scale: 2,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
        });
        return {
          title,
          dataUrl: canvas.toDataURL('image/png'),
          width: canvas.width,
          height: canvas.height,
        };
      } catch {
        return null;
      }
    },
    []
  );

  const handleDownload = React.useCallback(async () => {
    if (!filteredRecords.length || !trendMetricKey) return;

    setIsDownloading(true);
    try {
      const trendMetricLabel = selectedTrendMetric?.label ?? trendMetricKey;
      const trendPoints = aggregateTrend(filteredRecords, dateAccessor, trendMetricKey, trendGrain);

      const sections: ExcelSection[] = [];

      if (trendPoints.length) {
        sections.push({
          title: `Tendencia Temporal - ${trendMetricLabel} (${trendGrain})`,
          headers: ['Periodo', trendMetricLabel, 'Conteo'],
          rows: trendPoints.map(p => [p.label, p.metricValue, p.count]),
        });
      }

      dynamicRows.forEach(row => {
        const dimension = dimensionOptions.find(option => option.key === row.dimensionAccessor);
        const metric = metricOptions.find(option => option.key === row.metricKey);
        const dimensionLabel = dimension?.label ?? row.dimensionAccessor;
        const metricLabel = metric?.label ?? row.metricKey;
        const aggregated = aggregateByDimension(
          filteredRecords,
          row.dimensionAccessor,
          row.metricKey,
          row.topN
        );

        if (aggregated.rows.length) {
          sections.push({
            title: dimensionLabel,
            headers: [dimensionLabel, 'ID', metricLabel, 'Participacion (%)', 'Conteo'],
            rows: aggregated.rows.map(r => [
              r.label,
              r.secondaryLabel ?? '',
              r.metricValue,
              Math.round(r.sharePct * 100) / 100,
              r.count,
            ]),
          });
        }
      });

      if (!sections.length) {
        setIsDownloading(false);
        return;
      }

      // Capture chart images
      const chartImages: ChartImage[] = [];

      const trendImage = await captureChartImage(
        trendChartRef.current,
        `Tendencia Temporal - ${trendMetricLabel}`
      );
      if (trendImage) chartImages.push(trendImage);

      for (const row of dynamicRows) {
        const dimension = dimensionOptions.find(option => option.key === row.dimensionAccessor);
        const element = dynamicChartRefs.current.get(row.id) ?? null;
        const image = await captureChartImage(element, dimension?.label ?? row.dimensionAccessor);
        if (image) chartImages.push(image);
      }

      await downloadAdvancedAnalysisExcel(sections, 'analisis_avanzado.xlsx', chartImages);
    } finally {
      setIsDownloading(false);
    }
  }, [
    filteredRecords,
    trendMetricKey,
    selectedTrendMetric?.label,
    dateAccessor,
    trendGrain,
    dynamicRows,
    dimensionOptions,
    metricOptions,
    captureChartImage,
  ]);

  const hasData = records.length > 0 && metricOptions.length > 0;
  const hasFilteredData = filteredRecords.length > 0;
  const activePanelDimension = drillPath.length
    ? drillPath[drillPath.length - 1]?.dimensionAccessor
    : undefined;

  const onSelectDrillRow = React.useCallback(
    (payload: {
      dimensionAccessor: string;
      rowKey: string;
      label: string;
      secondaryLabel?: string;
    }) => {
      setDrillPath(prev =>
        toggleOrSetDrillStep(prev, {
          dimensionAccessor: payload.dimensionAccessor,
          valueKey: payload.rowKey,
          label: payload.label,
          secondaryLabel: payload.secondaryLabel,
        })
      );
    },
    []
  );

  const removeDrillStep = React.useCallback((index: number) => {
    setDrillPath(prev => prev.slice(0, index));
  }, []);

  const removeDrillByDimension = React.useCallback((dimensionAccessor: string) => {
    setDrillPath(prev => {
      const index = prev.findIndex(step => step.dimensionAccessor === dimensionAccessor);
      if (index === -1) return prev;
      return prev.slice(0, index);
    });
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!persistedStorageKey || !hasHydratedStorage || !metricOptions.length) {
      return;
    }

    const payload: PersistedAnalysisState = {
      trendMetricKey,
      trendUnit,
      trendGrain,
      forecastConfig,
      dynamicRows: dynamicRows.slice(0, 12),
      drillPath: drillPath.slice(0, 6),
    };

    try {
      window.localStorage.setItem(persistedStorageKey, JSON.stringify(payload));
    } catch {
      // Ignore storage write failures (quota/private mode).
    }
  }, [
    persistedStorageKey,
    hasHydratedStorage,
    metricOptions.length,
    trendMetricKey,
    trendUnit,
    trendGrain,
    forecastConfig,
    dynamicRows,
    drillPath,
  ]);

  const isAnalysisLoading = Boolean(loading);

  return (
    <section className="overflow-x-hidden border-y border-gray-200 bg-white sm:rounded-2xl sm:border sm:shadow-lg">
      <div className="flex flex-col gap-4 border-b border-gray-100 px-3 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Analisis avanzado</h2>
            <p className="text-sm text-gray-500">
              Tendencia temporal + rows dinamicos por dimension (tabla y grafico).
            </p>
          </div>
          <div className="flex items-center gap-2">
            {drillPath.length ? (
              <Button variant="outline" size="sm" onClick={() => setDrillPath([])}>
                <X className="mr-2 h-4 w-4" />
                Limpiar filtro
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleDownload()}
              disabled={!hasData || isAnalysisLoading || isDownloading}
            >
              {isDownloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {isDownloading ? 'Exportando...' : 'Descargar'}
            </Button>
          </div>
        </div>
      </div>

      {isAnalysisLoading ? (
        <div className="analysis-loading-pulse flex items-center justify-center gap-2 px-6 py-12 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
          Cargando analisis...
        </div>
      ) : !hasData ? (
        <div className="px-6 py-12 text-sm text-gray-500">
          Ejecuta una busqueda para ver tendencia y rows dinamicos.
        </div>
      ) : !hasFilteredData ? (
        <div className="px-6 py-12 text-sm text-gray-500">
          Sin datos para la seleccion actual. Limpia el filtro de drill-down para continuar.
        </div>
      ) : (
        <div className="space-y-4 px-2 py-4 sm:px-6 sm:py-6">
          <TrendRow
            records={filteredRecords}
            metricOptions={metricOptions}
            dateAccessor={dateAccessor}
            loading={isAnalysisLoading}
            metricKey={trendMetricKey}
            unit={trendUnit}
            grain={trendGrain}
            forecastConfig={forecastConfig}
            exchangeRateAccessor={exchangeRateAccessor || undefined}
            precomputedPoints={analyticsData?.trend?.points}
            onMetricChange={setTrendMetricKey}
            onUnitChange={setTrendUnit}
            onGrainChange={setTrendGrain}
            drillDepth={drillPath.length}
            chartRef={trendChartRef}
          />

          <DynamicRowSelector
            availableDimensions={dimensionOptions}
            selectedDimensionKeys={selectedDimensionKeys}
            drillPath={drillPath}
            onRemoveDrillStep={removeDrillStep}
            onAdd={addDynamicRow}
            onRemove={removeDynamicRowByDimension}
          />

          {dynamicRows.map(row => (
            <DynamicRowWithRef
              key={row.id}
              row={row}
              filteredRecords={filteredRecords}
              dimensionOptions={dimensionOptions}
              metricOptions={metricOptions}
              exchangeRateAccessor={exchangeRateAccessor || undefined}
              precomputedBackendDimension={analyticsData?.dimensions?.find(d => d.id === row.id)}
              onRemove={removeDynamicRow}
              onChange={patchDynamicRow}
              drillPath={drillPath}
              activePanelDimension={activePanelDimension}
              onSelectRow={onSelectDrillRow}
              onClearDimensionSelection={removeDrillByDimension}
              chartRefs={dynamicChartRefs}
            />
          ))}
        </div>
      )}
    </section>
  );
}
